import type { Paginated } from '@fleetcore/shared'
import type { NotificationChannel } from '@fleetcore/shared/telemetry/alerts'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import {
  acknowledgeAlert,
  commentOnAlert,
  createAlertRule,
  deleteAlertRule,
  escalateAlert,
  fetchAlert,
  fetchAlertCounts,
  fetchAlertRule,
  fetchAlertRules,
  fetchAlerts,
  fetchNotificationPreferences,
  previewAlertRule,
  reopenAlert,
  resolveAlert,
  saveNotificationPreferences,
  sendTestNotification,
  updateAlertRule,
  type ListAlertsParams,
  type ListRulesParams,
} from './api'
import type {
  AlertCountsView,
  AlertDetailView,
  AlertRuleView,
  AlertView,
  CreateAlertRuleInput,
  NotificationPreferenceView,
  RulePreviewView,
  TestNotificationResultView,
  UpdateAlertRuleInput,
  UpdateNotificationPreferenceInput,
} from './api-types'

/**
 * Cache policy.
 *
 * ─── THE FEED IS SHORT-LIVED, THE RULES ARE NOT ─────────────────────────────
 *
 * A rule changes when somebody edits it and never on its own, so it gets the same
 * five minutes every configuration surface in this app uses. The FEED is different:
 * it is the screen a dispatcher leaves open all afternoon, and an alert that fired
 * ninety seconds ago and is not on it yet is the "stayed silent" failure with a
 * client-side cause.
 *
 * The socket is what actually keeps it fresh (`use-alert-stream.ts`). This interval
 * is the floor underneath it — the same shape `features/map` uses, where polling
 * carries the screen whenever the transport is not live, and the screen says which
 * of the two it is rather than looking live while being stale.
 */
const FEED_STALE_MS = 15_000
const CONFIG_STALE_MS = 5 * 60_000

export const alertQueryKeys = {
  all: ['alerts'] as const,
  feed: (params: ListAlertsParams) => ['alerts', 'feed', params] as const,
  counts: ['alerts', 'counts'] as const,
  detail: (id: string) => ['alerts', 'detail', id] as const,
  rules: (params: ListRulesParams) => ['alerts', 'rules', params] as const,
  rule: (id: string) => ['alerts', 'rule', id] as const,
  preferences: ['alerts', 'preferences'] as const,
}

export function useAlertFeed(
  params: ListAlertsParams,
  options: { enabled?: boolean; pollWhenStale?: boolean } = {},
): UseQueryResult<Paginated<AlertView>> {
  return useQuery({
    queryKey: alertQueryKeys.feed(params),
    queryFn: () => fetchAlerts(params),
    enabled: options.enabled ?? true,
    staleTime: FEED_STALE_MS,
    // Only when the socket is NOT carrying the screen. A feed that both polls and
    // receives pushes issues a full page request for every alert that arrives, which
    // is the exact mistake `features/map` documents at fleet scale.
    refetchInterval: options.pollWhenStale === true ? FEED_STALE_MS : false,
    refetchOnWindowFocus: true,
  })
}

export function useAlertCounts(enabled = true): UseQueryResult<AlertCountsView> {
  return useQuery({
    queryKey: alertQueryKeys.counts,
    queryFn: fetchAlertCounts,
    enabled,
    staleTime: FEED_STALE_MS,
    refetchOnWindowFocus: true,
  })
}

export function useAlertDetail(id: string | null): UseQueryResult<AlertDetailView> {
  return useQuery({
    queryKey: alertQueryKeys.detail(id ?? ''),
    queryFn: () => fetchAlert(id as string),
    enabled: id !== null && id.length > 0,
    staleTime: FEED_STALE_MS,
  })
}

export function useAlertRules(
  params: ListRulesParams = {},
): UseQueryResult<Paginated<AlertRuleView>> {
  return useQuery({
    queryKey: alertQueryKeys.rules(params),
    queryFn: () => fetchAlertRules(params),
    staleTime: CONFIG_STALE_MS,
  })
}

export function useAlertRule(id: string | null): UseQueryResult<AlertRuleView> {
  return useQuery({
    queryKey: alertQueryKeys.rule(id ?? ''),
    queryFn: () => fetchAlertRule(id as string),
    enabled: id !== null && id.length > 0,
    staleTime: CONFIG_STALE_MS,
  })
}

export function useNotificationPreferences(): UseQueryResult<
  readonly NotificationPreferenceView[]
> {
  return useQuery({
    queryKey: alertQueryKeys.preferences,
    queryFn: fetchNotificationPreferences,
    staleTime: CONFIG_STALE_MS,
  })
}

/**
 * Every workflow move changes the alert, the feed row and the header counts at once.
 *
 * Invalidating the whole `alerts` tree is blunt and correct here: acknowledging moves
 * a row between the "open" and "acknowledged" tallies, and enumerating the three key
 * families is how one of them gets forgotten the next time a screen is added. The
 * mutation already returns the fresh detail, so the drawer redraws immediately and
 * the refetches settle behind it.
 */
function useAlertInvalidation() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: alertQueryKeys.all })
}

export interface AcknowledgeVariables {
  readonly id: string
  readonly comment: string
}

export function useAcknowledgeAlert(): UseMutationResult<
  AlertDetailView,
  Error,
  AcknowledgeVariables
> {
  const invalidate = useAlertInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: AcknowledgeVariables) => acknowledgeAlert(id, comment),
    onSuccess: (detail) => {
      client.setQueryData(alertQueryKeys.detail(detail.id), detail)
      void invalidate()
    },
  })
}

export interface ResolveVariables {
  readonly id: string
  readonly comment?: string
}

export function useResolveAlert(): UseMutationResult<AlertDetailView, Error, ResolveVariables> {
  const invalidate = useAlertInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: ResolveVariables) => resolveAlert(id, comment),
    onSuccess: (detail) => {
      client.setQueryData(alertQueryKeys.detail(detail.id), detail)
      void invalidate()
    },
  })
}

export function useReopenAlert(): UseMutationResult<AlertDetailView, Error, AcknowledgeVariables> {
  const invalidate = useAlertInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: AcknowledgeVariables) => reopenAlert(id, comment),
    onSuccess: (detail) => {
      client.setQueryData(alertQueryKeys.detail(detail.id), detail)
      void invalidate()
    },
  })
}

export interface EscalateVariables {
  readonly id: string
  readonly comment: string
  readonly escalatedTo?: string
}

export function useEscalateAlert(): UseMutationResult<AlertDetailView, Error, EscalateVariables> {
  const invalidate = useAlertInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment, escalatedTo }: EscalateVariables) =>
      escalateAlert(id, comment, escalatedTo),
    onSuccess: (detail) => {
      client.setQueryData(alertQueryKeys.detail(detail.id), detail)
      void invalidate()
    },
  })
}

export function useCommentOnAlert(): UseMutationResult<
  AlertDetailView,
  Error,
  AcknowledgeVariables
> {
  const invalidate = useAlertInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: AcknowledgeVariables) => commentOnAlert(id, comment),
    onSuccess: (detail) => {
      client.setQueryData(alertQueryKeys.detail(detail.id), detail)
      void invalidate()
    },
  })
}

export function useCreateAlertRule(): UseMutationResult<
  AlertRuleView,
  Error,
  CreateAlertRuleInput
> {
  const invalidate = useAlertInvalidation()
  return useMutation({ mutationFn: createAlertRule, onSuccess: invalidate })
}

export interface UpdateRuleVariables {
  readonly id: string
  readonly input: UpdateAlertRuleInput
}

export function useUpdateAlertRule(): UseMutationResult<AlertRuleView, Error, UpdateRuleVariables> {
  const invalidate = useAlertInvalidation()
  return useMutation({
    mutationFn: ({ id, input }: UpdateRuleVariables) => updateAlertRule(id, input),
    onSuccess: invalidate,
  })
}

export function useDeleteAlertRule(): UseMutationResult<void, Error, string> {
  const invalidate = useAlertInvalidation()
  return useMutation({ mutationFn: deleteAlertRule, onSuccess: invalidate })
}

export interface PreviewVariables {
  readonly input: CreateAlertRuleInput
  readonly days: number
}

/**
 * The dry run writes nothing, so it invalidates nothing.
 *
 * Invalidating here would make the rules list refetch for an operation that by
 * construction changed no data — the same reasoning the fuel import's dry run
 * follows.
 */
export function usePreviewAlertRule(): UseMutationResult<RulePreviewView, Error, PreviewVariables> {
  return useMutation({
    mutationFn: ({ input, days }: PreviewVariables) => previewAlertRule(input, days),
  })
}

export function useSaveNotificationPreferences(): UseMutationResult<
  readonly NotificationPreferenceView[],
  Error,
  readonly UpdateNotificationPreferenceInput[]
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: saveNotificationPreferences,
    onSuccess: (result) => client.setQueryData(alertQueryKeys.preferences, result),
  })
}

/**
 * The test send. Invalidates nothing — it changes no stored state, and its whole value
 * is the answer it returns.
 */
export function useTestNotification(): UseMutationResult<
  TestNotificationResultView,
  Error,
  { channel: NotificationChannel; phone?: string }
> {
  return useMutation({
    mutationFn: ({ channel, phone }: { channel: NotificationChannel; phone?: string }) =>
      sendTestNotification(channel, phone),
  })
}
